import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import  LogoContainer from '../../../../../shared/components/LogoContainer'
import { currencyOptions} from '../../../../../../utils/constants/currencyOptions';
import { createAccount } from '../../../../../../app/store/slices/accountSlice';
import AddAnotherList from '../../../../../shared/components/AddAnotherList';
import  Spinner  from '../../../../../shared/components/Spinner';
import {accountTypes} from '../../../../../../utils/constants/dashboardConstants';

const CashAccountSetup = () => {
  const navigate = useNavigate();
  const location = useLocation(); 
  const dispatch = useDispatch();
  const [accounts, setAccounts] = useState([]);
  const [currentAccount, setCurrentAccount] = useState({
    name: '',
    type: '',
    currency: location.state?.currency || JSON.parse(localStorage.getItem('onboardingCanvas'))?.currency
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCanvasId, setSelectedCanvasId] = useState(location.state?.canvasId || '');

  useEffect(() => {
    if (!selectedCanvasId){
      setSelectedCanvasId(JSON.parse(localStorage.getItem('onboardingCanvas'))?._id)
    }
  }, [selectedCanvasId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentAccount({ ...currentAccount, [name]: value });
  };

  const handleAddAccount = useCallback(async () => {
    if (currentAccount.name && currentAccount.type && currentAccount.currency && selectedCanvasId) {
      try {
        await dispatch(createAccount({
          canvasId: JSON.parse(localStorage.getItem('onboardingCanvas'))?._id || selectedCanvasId,
          accountData: currentAccount
        })).unwrap();
        setAccounts([...accounts, currentAccount]);
        setCurrentAccount({ name: '', type: '', currency: location.state?.currency || JSON.parse(localStorage.getItem('onboardingCanvas'))?.currency });
      } catch (err) {
        console.error('Failed to create account:', err);
      }
    }
  }, [currentAccount, selectedCanvasId, accounts, dispatch, location.state?.currency]);

  const handleContinue = useCallback(async () => {
    setIsProcessing(true);
    try {
      if (currentAccount.name && currentAccount.type && currentAccount.currency && selectedCanvasId) {
        await dispatch(createAccount({
          canvasId: selectedCanvasId,
          accountData: currentAccount
        })).unwrap();
        setAccounts(prev => [...prev, currentAccount]);
        setCurrentAccount({ name: '', type: '', currency: location.state?.currency || '' });
        
        navigate('/income', { state: { accountId: currentAccount._id, canvasId: selectedCanvasId } });
        return;
      }
      
      if (accounts.length > 0) {
        const lastAccount = accounts[accounts.length - 1];
        navigate('/income', { state: { accountId: lastAccount._id, canvasId: selectedCanvasId } });
      } else {
        navigate('/income', { state: { canvasId: selectedCanvasId } });
      }
    } catch (err) {
      console.error('Failed to create account:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [currentAccount, selectedCanvasId, accounts, dispatch, navigate, location.state?.currency]);


  const handleTouchAddAccount = useCallback((e) => {
    e.preventDefault();
    handleAddAccount();
  }, [handleAddAccount]);

  const handleTouchContinue = useCallback((e) => {
    e.preventDefault();
    handleContinue();
  }, [handleContinue]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <LogoContainer/>
      <div className='card-setup'>
        <h2 className='txt-page-heading'>Create Your Cash Accounts</h2>
        <p className="p-tag-below-heading text-center">
          Bank accounts and wallets where you usually hold your cash, you can create as many as you want.
          At the moment Cash Canvas does not connect to any of your bank accounts, this function will be
          added at a later stage.
        </p>
        <div className="w-full flex flex-col items-center">
          <div className="space-y-4 w-full max-w-lg mx-auto">
            {/* Account Name */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="accountName" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Account Name
              </label>
              <input
                type="text"
                id="accountName"
                name="name"
                value={currentAccount.name}
                onChange={handleInputChange}
                placeholder="Your Account Name"
                className="input-field flex-1 w-full max-w-[400px]"
              />
            </div>
            {/* Account Type */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="accountType" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Account Type
              </label>
              <select
                id="accountType"
                name="type"
                value={currentAccount.type}
                onChange={handleInputChange}
                className="input-field flex-1 w-full max-w-[400px] text-gray-400"
              >
                <option value="">Select Account Type</option>
                {accountTypes.map((type) => (
                  <option key={type.key} value={type.value} className="capitalize">
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Currency */} 
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="currency" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Currency
              </label>
              <select
                id="currency"
                name="currency"
                value={currentAccount.currency}
                onChange={handleInputChange}
                className="input-field flex-1 w-full max-w-[400px] text-gray-400"
              >
                <option value="">Select Currency</option>
                {currencyOptions.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name} ({currency.symbol})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Centered action buttons */}
          <div className="mt-8 flex flex-row justify-center gap-6 w-full max-w-lg">
            <button
              type="button"
              className="btn-back flex-1"
              onClick={() => {
                setCurrentAccount({ name: '', type: '', currency: location.state?.currency || '' });
                if (accounts.length > 0) {
                  const lastAccount = accounts[accounts.length - 1];
                  navigate('/income', { state: { accountId: lastAccount._id, canvasId: selectedCanvasId } });
                } else {
                  navigate('/income', { state: { canvasId: selectedCanvasId } });
                }
              }}
            >
              Skip
            </button>
            <button
              type="button"
              className="btn-continue flex-1"
              onClick={handleAddAccount}
              onTouchEnd={handleTouchAddAccount}
            >
              Add Another Account
            </button>
          </div>
        </div>
        
          <AddAnotherList
            title="Your Accounts"
            items={accounts}
            renderItem={(account) => (
              <>
                <div>
                  <div className="font-medium">{account.name}</div>
                  <div className="text-sm text-gray-500 capitalize">{account.type}</div>
                </div>
                <div className="text-sm text-gray-500">
                  {account.currency || 'No currency selected'}
                </div>
              </>
            )}
          />

        <div className="flex flex-col md:flex-row justify-between gap-4 mt-8">
          <button
            className="btn-back"
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <button
            className="btn-continue"
            onClick={handleContinue}
            onTouchEnd={handleTouchContinue}
          >
            {isProcessing ? (
              <Spinner text="Processing..."/>
            ) : (
              ' Save & Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashAccountSetup;